import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Calendar,
  Save,
  RotateCcw,
  Search
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from 'react-toastify'
import SACMasterModal from '../../components/SACMasterModal/SACMasterModal'
import '../../components/SACMasterModal/SACMasterModal.css'
import serviceMasterService from '../../services/serviceMasterService'

const ServiceMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user } = useAuth()
  
  // Get the Service data from navigation state or URL params
  const serviceData = location.state?.serviceData || {
    id: 1,
    serviceCode: 'SER/0004',
    serviceDescription: 'Support & Maintenance',
    active: 'Yes',
    selectSAC: 'SAC001',
    gstRate: '18.00',
    igstRate: '18.00',
    cgstRate: '9.00',
    sgstRate: '9.00'
  }

  const [formData, setFormData] = useState({
    serviceCode: '',
    serviceDescription: '',
    active: 'Yes',
    selectSAC: '',
    gstRate: '',
    igstRate: '',
    cgstRate: '',
    sgstRate: ''
  })
  const [showSACModal, setShowSACModal] = useState(false)

  // Pre-fill form data when component mounts
  useEffect(() => {
    setFormData({
      serviceCode: serviceData.serviceCode || '',
      serviceDescription: serviceData.serviceDescription || '',
      active: serviceData.active || 'Yes',
      selectSAC: serviceData.selectSAC || '',
      gstRate: serviceData.gstRate || '',
      igstRate: serviceData.igstRate || '',
      cgstRate: serviceData.cgstRate || '',
      sgstRate: serviceData.sgstRate || ''
    })
  }, [serviceData])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    try {
      console.log('Updating Service Master data:', formData)
      
      // Call the service master API to update the data
      const updateResponse = await serviceMasterService.updateService(serviceData._id, formData)
      
      if (updateResponse && updateResponse.success) {
        toast.success('Service Master updated successfully!')
        navigate('/sales/service-master')
      } else {
        toast.error(updateResponse?.message || 'Failed to update Service Master')
      }
    } catch (error) {
      console.error('Error updating Service Master:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        navigate('/login')
      } else if (error.response?.status === 404) {
        toast.error('Service Master not found')
      } else {
        toast.error('Failed to update Service Master')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      serviceCode: serviceData.serviceCode || '',
      serviceDescription: serviceData.serviceDescription || '',
      active: serviceData.active || 'Yes',
      selectSAC: serviceData.selectSAC || '',
      gstRate: serviceData.gstRate || '',
      igstRate: serviceData.igstRate || '',
      cgstRate: serviceData.cgstRate || '',
      sgstRate: serviceData.sgstRate || ''
    })
  }

  const handleBack = () => {
    navigate('/sales/service-master')
  }

  const handleSACSearch = () => {
    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      toast.error('Please log in to access SAC master data')
      return
    }
    
    // Check if token exists
    const token = localStorage.getItem('token')
    if (!token) {
      toast.error('Authentication token not found. Please log in again.')
      return
    }
    
    setShowSACModal(true)
  }

  const handleSACSelect = (selectedSAC) => {
    setFormData(prev => ({
      ...prev,
      selectSAC: selectedSAC.sacCode,
      gstRate: selectedSAC.gstRate?.toString() || '',
      igstRate: selectedSAC.igstRate?.toString() || '',
      cgstRate: selectedSAC.cgstRate?.toString() || '',
      sgstRate: selectedSAC.sgstRate?.toString() || ''
    }))
    setShowSACModal(false)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Service Master',
      path: '/sales/service-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Service Master - Edit</h1>
          <p>Edit service master data</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Service Master</h2>
        </div>
          
        <form className="form-content">
          {/* Row 1: Service Code, Service Description, Active, Select SAC */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="serviceCode" className="form-label required">
                Service Code
              </label>
              <input
                type="text"
                id="serviceCode"
                name="serviceCode"
                value={formData.serviceCode}
                readOnly
                className="form-input"
                placeholder="Enter Service Code"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="serviceDescription" className="form-label required">
                Service Description
              </label>
              <input
                type="text"
                id="serviceDescription"
                name="serviceDescription"
                value={formData.serviceDescription}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter service description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="active" className="form-label required">
                Active
              </label>
              <select
                id="active"
                name="active"
                value={formData.active}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="selectSAC" className="form-label required">
                Select SAC
              </label>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  id="selectSAC"
                  name="selectSAC"
                  value={formData.selectSAC}
                  readOnly
                  className="form-input search-input"
                  placeholder="Select SAC code from modal"
                  style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                />
                <button
                  type="button"
                  className="search-button"
                  title="Search SAC"
                  onClick={handleSACSearch}
                >
                  <Search size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Row 2: GST Rate, IGST Rate, CGST Rate, SGST Rate */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="gstRate" className="form-label">
                GST Rate
              </label>
              <input
                type="number"
                id="gstRate"
                name="gstRate"
                value={formData.gstRate}
                className="form-input"
                step="0.01"
                min="0"
                placeholder="0.00"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="igstRate" className="form-label">
                IGST Rate
              </label>
              <input
                type="number"
                id="igstRate"
                name="igstRate"
                value={formData.igstRate}
                className="form-input"
                step="0.01"
                min="0"
                placeholder="0.00"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cgstRate" className="form-label">
                CGST Rate
              </label>
              <input
                type="number"
                id="cgstRate"
                name="cgstRate"
                value={formData.cgstRate}
                className="form-input"
                step="0.01"
                min="0"
                placeholder="0.00"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="sgstRate" className="form-label">
                SGST Rate
              </label>
              <input
                type="number"
                id="sgstRate"
                name="sgstRate"
                value={formData.sgstRate}
                className="form-input"
                step="0.01"
                min="0"
                placeholder="0.00"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Update
            </button>
          </div>
        </form>
      </div>

      {/* SAC Master Modal */}
      <SACMasterModal
        isOpen={showSACModal}
        onClose={() => setShowSACModal(false)}
        onSelectSAC={handleSACSelect}
        selectedSACCode={formData.selectSAC}
      />
    </div>
  )
}

export default ServiceMasterEdit